/**
 * Keeps Bootstrap Dropdown instances in sync with the toggles currently in the
 * DOM, WITHOUT recreating instances that are still live.
 *
 * That last part is the whole point. Bootstrap resolves its menu element once,
 * in the constructor, by looking for a SIBLING of the toggle. The hikes filter
 * menus are moved out of the scrolling row after construction (see
 * filterMenuLayer.ts), so re-constructing an instance later finds no sibling
 * menu, leaves `_menu` null, and every subsequent click throws inside
 * `_isShown()` — "null is not an object (evaluating 'this._menu.classList')".
 *
 * The hikes list re-renders on every search, so this is called repeatedly: it
 * must reap instances whose toggle has been detached (or they pile up in
 * Bootstrap's element map) while leaving the surviving ones untouched.
 */

export interface Disposable {
  dispose(): void;
}

export interface Tracked<T extends Disposable> {
  el: Element;
  instance: T;
}

const TOGGLE = '[data-bs-toggle="dropdown"]';

export interface SyncResult<T extends Disposable> {
  tracked: Tracked<T>[];
  /** Toggles that gained a brand-new instance on this pass. */
  created: Element[];
  /** Instances disposed because their toggle had left the DOM. */
  reaped: number;
}

export function syncDropdownRegistry<T extends Disposable>(
  tracked: Tracked<T>[],
  root: ParentNode,
  createInstance: (el: Element) => T,
): SyncResult<T> {
  let reaped = 0;
  const kept = tracked.filter(({ el, instance }) => {
    if (el.isConnected) return true;
    try {
      instance.dispose();
    } catch {
      /* already torn down */
    }
    reaped += 1;
    return false;
  });

  const known = new Set(kept.map((t) => t.el));
  const created: Element[] = [];
  root.querySelectorAll(TOGGLE).forEach((el) => {
    if (known.has(el)) return;
    kept.push({ el, instance: createInstance(el) });
    created.push(el);
  });

  return { tracked: kept, created, reaped };
}
